import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Button, Stack, Typography, Alert, CircularProgress, Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import FeatureList from '../components/feature-map/FeatureList';
import FeatureEditor from '../components/feature-map/FeatureEditor';
import SaveBufferIndicator from '../components/matrix/SaveBufferIndicator';
import { getProducts, getCatalog, getFeatures, saveFeatures, getContacts } from '../api/featureMap';
import { slugify } from '../utils/slugify';

export default function FeatureMapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // ?project=<slug> 으로 진입 시 해당 Project 의 Features 만 보여줌.
  // ProjectsPage 카드 클릭 → 본 페이지에서 해당 Project 의 Feature 목록 확인.
  const projectFilter = searchParams.get('project') || '';

  const [features, setFeatures] = useState([]);
  const [originalFeatures, setOriginalFeatures] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, c, f, ct] = await Promise.all([getProducts(), getCatalog(), getFeatures(), getContacts()]);
      setProducts(p.products || []);
      setCatalog(c.catalog || []);
      const fresh = f.features || [];
      setFeatures(fresh);
      setOriginalFeatures(JSON.parse(JSON.stringify(fresh)));
      setMembers(ct.membersFlat || []);
      setDirty(false);
      setStatusMsg(`소프트웨어 ${p.count} · Feature ${(f.features || []).length} · Catalog ${c.count}`);
    } catch (e) {
      setError(e.message);
      setStatusMsg('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // project 필터 적용 결과. URL ?project= 가 없으면 전체.
  const visibleFeatures = useMemo(() => {
    if (!projectFilter) return features;
    return features.filter((f) => f.project === projectFilter);
  }, [features, projectFilter]);

  // 변경 카운트 — Feature id 단위 diff (추가 / 삭제 / 수정).
  // _new 같은 내부 플래그 제외하고 비교.
  const changeCount = useMemo(() => {
    const norm = (f) => {
      const { _new, _idEdited, ...rest } = f;
      return JSON.stringify(rest);
    };
    const origMap = new Map(originalFeatures.map((f) => [f.id, norm(f)]));
    const currMap = new Map(features.map((f) => [f.id, norm(f)]));
    let count = 0;
    for (const [id, json] of currMap) {
      if (origMap.get(id) !== json) count++;
    }
    for (const id of origMap.keys()) {
      if (!currMap.has(id)) count++;
    }
    return count;
  }, [features, originalFeatures]);

  // 필터 적용 시 selectedIdx 의 항목이 숨겨졌다면 -1 로.
  useEffect(() => {
    if (selectedIdx < 0) return;
    const current = features[selectedIdx];
    if (!current) return;
    if (projectFilter && current.project !== projectFilter) {
      setSelectedIdx(-1);
    }
  }, [projectFilter, features, selectedIdx]);

  // FEAT-006 — ProductDetailPage 의 관련 Feature 카드 클릭 시 `?focus=<FEAT-ID>` 로 진입.
  // features 로딩 완료 후 해당 Feature 의 index 를 자동 선택하고 URL 의 focus 쿼리는 정리한다.
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId || features.length === 0) return;
    const idx = features.findIndex((f) => f.id === focusId);
    if (idx >= 0) setSelectedIdx(idx);
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  const projectMeta = useMemo(
    () => catalog.find((c) => c.slug === projectFilter),
    [catalog, projectFilter],
  );

  const handleClearProjectFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('project');
    setSearchParams(next, { replace: true });
  };

  // beforeunload 가드
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const handleNewFeature = () => {
    const tempId = `NEW-FEATURE-${features.length + 1}`;
    // 현재 project 필터가 있으면 신규 Feature 의 project 를 자동 prefill.
    const newFeature = {
      id: tempId, name: '', status: 'planned', components: [], timeline: {}, _new: true,
      ...(projectFilter ? { project: projectFilter } : {}),
    };
    const next = [...features, newFeature];
    setFeatures(next);
    // visibleFeatures 기준 selectedIdx — 신규 항목은 항상 마지막.
    setSelectedIdx(next.length - 1);
    setDirty(true);
  };

  const handleSelect = (feature) => {
    if (dirty && !window.confirm('편집 내용이 저장되지 않았습니다. 이동하시겠습니까?')) return;
    setSelectedIdx(features.indexOf(feature));
  };

  const updateFeature = (mutator) => {
    setFeatures((prev) => {
      const next = [...prev];
      next[selectedIdx] = mutator({ ...next[selectedIdx] });
      return next;
    });
    setDirty(true);
  };

  const handleChangeField = (key, val) => {
    updateFeature((f) => {
      // 신규 Feature 의 ID 수동 편집 — 대문자 자동 변환 + 편집 플래그 기록
      if (key === 'id' && f._new) {
        f.id = (val || '').toUpperCase();
        f._idEdited = true;
        return f;
      }
      f[key] = val;
      // 새 Feature 이름 변경 시 ID 자동 슬러그 — 사용자가 ID 를 수동 편집한 적 없을 때만
      if (key === 'name' && f._new && !f._idEdited) {
        const slug = slugify(val);
        f.id = slug || `NEW-FEATURE-${selectedIdx + 1}`;
      }
      return f;
    });
  };

  const handleChangeTimeline = (key, val) => {
    updateFeature((f) => {
      f.timeline = { ...(f.timeline || {}), [key]: val };
      return f;
    });
  };

  const handleCompChange = (ci, key, val) => {
    updateFeature((f) => {
      const comps = [...(f.components || [])];
      comps[ci] = { ...comps[ci], [key]: val };
      f.components = comps;
      return f;
    });
  };

  const handleAddComp = () => {
    updateFeature((f) => {
      f.components = [...(f.components || []), { product: '', version: '', status: 'planned', notes: '' }];
      return f;
    });
  };

  const handleRemoveComp = (ci) => {
    updateFeature((f) => {
      f.components = (f.components || []).filter((_, i) => i !== ci);
      return f;
    });
  };

  const handleDelete = () => {
    const f = features[selectedIdx];
    if (!f) return;
    if (!window.confirm(`Feature "${f.id}" 삭제? (저장 전까지 되돌릴 수 있음)`)) return;
    const next = features.filter((_, i) => i !== selectedIdx);
    setFeatures(next);
    setSelectedIdx(-1);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 저장 전 내부 플래그 제거
      const payload = features.map(({ _new, _idEdited, ...rest }) => rest);
      const result = await saveFeatures(payload);
      // 저장된 features는 모두 lock — _new 해제 + originalFeatures 갱신 (changeCount 0 으로)
      const lockedFeatures = features.map((f) => ({ ...f, _new: false }));
      setFeatures(lockedFeatures);
      setOriginalFeatures(JSON.parse(JSON.stringify(lockedFeatures)));
      setDirty(false);
      const shortSha = (result.commitSha || '').slice(0, 7);
      setStatusMsg(`✅ main 직접 반영 — commit ${shortSha}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selected = features[selectedIdx];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h2">Features</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {loading && <CircularProgress size={20} />}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{statusMsg}</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewFeature}>새 Feature</Button>
        <Button startIcon={<RefreshIcon />} onClick={reload}>새로고침</Button>
        {projectFilter && (
          <Chip
            label={
              <span>
                Project: <b>{projectMeta?.name || projectFilter}</b>
                <span style={{ marginLeft: 6, opacity: 0.6, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                  {projectFilter}
                </span>
                <span style={{ marginLeft: 8, opacity: 0.7 }}>· {visibleFeatures.length}건</span>
              </span>
            }
            color="primary"
            variant="outlined"
            onDelete={handleClearProjectFilter}
            deleteIcon={<ClearIcon />}
          />
        )}
      </Stack>

      {/* 페이지 레벨 저장 바 — 우측 Editor 미선택 시 (예: 삭제 직후) 에도 저장 가능. */}
      <SaveBufferIndicator changeCount={changeCount} saving={saving} onSave={handleSave} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 2, height: 'calc(100vh - 320px)', minHeight: 500 }}>
        <Paper sx={{ overflowY: 'auto' }}>
          <FeatureList
            features={visibleFeatures}
            selectedFeature={selectedIdx >= 0 ? features[selectedIdx] : null}
            onSelect={handleSelect}
            emptyMessage={projectFilter
              ? <>이 Project (<b>{projectMeta?.name || projectFilter}</b>) 에 매핑된 Feature 가 없습니다.<br /><br /><b>+ 새 Feature</b> 로 추가하세요.</>
              : undefined}
          />
        </Paper>
        {selected ? (
          <FeatureEditor
            feature={selected}
            products={products}
            catalog={catalog}
            members={members}
            onChangeField={handleChangeField}
            onChangeTimeline={handleChangeTimeline}
            onCompChange={handleCompChange}
            onAddComp={handleAddComp}
            onRemoveComp={handleRemoveComp}
            onDelete={handleDelete}
            onSave={handleSave}
            saving={saving}
          />
        ) : (
          <Paper sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
            왼쪽에서 Feature를 선택하거나, <b>+ 새 Feature</b>를 눌러 추가하세요.
          </Paper>
        )}
      </Box>
    </Box>
  );
}
